import type { ReactNode } from 'react';

export interface StatCardProps {
  /** Icon glyph. Pass a self-contained icon (already carrying its own
   * backdrop, e.g. `AllEventsIcon`) with `tinted={false}`; otherwise it's
   * wrapped in the shared soft-green tinted square. */
  icon: ReactNode;
  /** Wrap `icon` in the pale-green rounded square. Defaults on. */
  tinted?: boolean;
  label: string;
  value: number | string;
  /** Small muted line under the value. `stacked` layout only. */
  caption?: string;
  /**
   * `row` (default): icon + label on the left, value on the right, one row -
   * the original shape (Dashboard, Matching KPIs, Partner Offers).
   * `stacked`: icon + label on top, a large value below, an optional caption
   * under that - a vertical KPI tile (MYN-196's Content Management page).
   */
  layout?: 'row' | 'stacked';
  className?: string;
  /**
   * Makes the card a toggle, e.g. a summary card that filters the table below
   * it. `row` layout only. Rendered as a button with `aria-pressed`.
   */
  onSelect?: () => void;
  /** Whether a selectable card's filter is the one applied. */
  selected?: boolean;
}

/** Small white stat tile, either a single icon/label/value row or a stacked KPI tile. */
export function StatCard({
  icon,
  tinted = true,
  label,
  value,
  caption,
  layout = 'row',
  className = '',
  onSelect,
  selected = false,
}: StatCardProps) {
  const iconEl = tinted ? (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 items-center justify-center rounded-[0.625rem] bg-green/10 text-green"
    >
      {icon}
    </span>
  ) : (
    <span aria-hidden="true">{icon}</span>
  );

  if (layout === 'stacked') {
    return (
      <div className={`flex flex-col gap-3 rounded-card bg-white p-5 shadow-card-soft ${className}`.trim()}>
        <div className="flex items-center gap-2.5">
          {iconEl}
          <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-slate">{label}</p>
        </div>
        <p className="text-[28px] font-semibold text-navy">{value}</p>
        {caption && <p className="text-[11px] text-slate">{caption}</p>}
      </div>
    );
  }

  const body = (
    <>
      <div className="flex items-center gap-2.5">
        {iconEl}
        <p className="text-p5 text-black">{label}</p>
      </div>
      <p className="text-[1.5rem] font-medium text-navy">{value}</p>
    </>
  );
  const box = 'flex items-center justify-between gap-4 rounded-card bg-white p-6 shadow-card-soft';

  if (onSelect) {
    // A plain <button>: the whole tile is the control, and no `Button`
    // variant has a card's box model (the narrow exception in mnr-standards).
    return (
      <button
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className={`${box} cursor-pointer text-left ring-green transition-shadow hover:shadow-card focus-visible:ring-2 focus-visible:outline-none ${selected ? 'ring-2' : ''} ${className}`.trim()}
      >
        {body}
      </button>
    );
  }

  return <div className={`${box} ${className}`.trim()}>{body}</div>;
}
